import { Fragment, FunctionComponent, JSX } from 'preact';
import { useCallback, useEffect, useRef } from 'preact/hooks';

import { PortalOverlay } from '../PortalOverlay/PortalOverlay';
import { Typography } from '../Typography/Typography';
import { Button } from '../Button/Button';
import { useTranslation } from '../../../../utils/useTranslation';
import ns from '../../translations';

export interface ReportDialogProps {
  variant?: 'error' | 'feedback';
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: { name: string; email: string; comments: string }) => void;
}

export const ReportDialog: FunctionComponent<ReportDialogProps> = ({
  variant = 'error',
  isOpen,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation(ns);
  const ref = useRef<HTMLDivElement>(null!);
  const formRef = useRef<HTMLFormElement>(null!);

  useEffect(() => {
    if (!isOpen) {
      formRef.current.reset();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClick = (e: MouseEvent) => {
      if (e.target && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('click', handleClick, { passive: true });
    document.addEventListener('keydown', handleKeyDown, { passive: true });

    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const handleSubmit: JSX.GenericEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();

      const formData = new FormData(e.target as HTMLFormElement);

      onSubmit({
        name: formData.get('name') as string,
        email: formData.get('email') as string,
        comments: formData.get('comments') as string,
      });
    },
    [onSubmit]
  );

  return (
    <PortalOverlay isOpen={isOpen}>
      <div className="vgp-report-dialog vgp-skin" ref={ref}>
        <header>
          {variant === 'error' ? (
            <Fragment>
              <Typography variant="title" size="medium" color="primary" bold>
                {t("It looks like we're having issues.")}
              </Typography>
              <Typography variant="label" size="medium" color="primary">
                {t(
                  "Our team has been notified. If you'd like to help, tell us what happened below."
                )}
              </Typography>
            </Fragment>
          ) : (
            <Typography variant="label" size="medium">
              {t('Send feedback')}
            </Typography>
          )}
        </header>
        <form onSubmit={handleSubmit} ref={formRef}>
          <div className="vgp-report-dialog-form-group">
            <Typography variant="label" color="primary" size="small">
              {t('Name')}
            </Typography>
            <input name="name" id="name" placeholder="Jane Bloggs" required />
          </div>
          <div className="vgp-report-dialog-form-group">
            <Typography variant="label" color="primary" size="small">
              Email
            </Typography>
            <input
              name="email"
              id="email"
              type="email"
              placeholder="jane@example.com"
              required
            />
          </div>
          <div className="vgp-report-dialog-form-group">
            <Typography variant="label" color="primary" size="small">
              {t('What happened?')}
            </Typography>
            <textarea
              name="comments"
              id="comments"
              rows={5}
              placeholder={t('I clicked "X" and then "Confirm"', undefined, {
                noJSX: true,
              })}
              required
            />
          </div>
          <div className="vgp-report-dialog-controls">
            <Button
              color="tertiary"
              size="medium"
              type="button"
              onClick={onClose}
            >
              {t('Cancel')}
            </Button>
            <Button color="primary" size="medium" type="submit">
              {t('Report')}
            </Button>
          </div>
        </form>
      </div>
    </PortalOverlay>
  );
};
